/** Sign-in copy only. These keys never grant authentication or application access. */
export const SIGN_IN_COPY = {
  cancelled: "",
  connection: "Can't reach the sign-in service. Check your connection and try again.",
  unreachable: "Kite is restarting or unreachable. Try again in a minute.",
  timed_out: "Sign-in timed out. Try again.",
  browser_interaction: "Couldn't open the sign-in window. Allow it in your browser and try again.",
  provider_configuration:
    "This sign-in method needs attention from your administrator. Contact them or choose another available method.",
  account_refused:
    "This account can't sign in here. Try your work account or contact your administrator.",
  verification_failed: "We couldn't verify this sign-in. Start again.",
  signin_failed: "Sign-in didn't finish. Try again.",
  invalid_email: "Enter your work email.",
  bad_credentials: "Wrong email or password.",
  ticket_expired: "This sign-in has expired. Start again.",
  code_wrong: "That code didn't work.",
  code_expired: "That code has expired. Send a new one.",
  codes_locked: "Too many wrong codes. Use a passkey, or ask your administrator to unlock codes.",
  passkey_required: "This sign-in needs a passkey. Sign in another way and add one first.",
  passkey_failed: "That passkey didn't work. Try again.",
  challenge_expired: "This passkey request expired. Try again.",
  invalid_credential: "The passkey response could not be read.",
  invalid_handoff: "This sign-in link is not valid. Start again from the app.",
  rate_limited: "Too many attempts. Wait before trying again.",
  method_not_allowed: "Use a sign-in method allowed by your organization.",
  not_invited: "You haven't been invited. Ask your administrator.",
  user_disabled: "Your account has been disabled.",
  workspace_required: "Sign in with your company's Google Workspace account.",
  identity_link_required:
    "This account needs a verified identity link before Google sign-in is available.",
  account_mismatch: "This Google account isn't the one linked to this email. Sign in another way.",
  google_email_unverified: "Your Google account's email isn't verified.",
  update_required: "Update Kite to sign in.",
  admin_required: "That page is for admins.",
  session_signed_out: "Your session was signed out.",
  reauth_required: "Sign in again to continue.",
  session_revoked: "You were signed out by an administrator.",
  session_ended: "Your session ended. Sign in again.",
} as const;

export type SignInErrorKey = keyof typeof SIGN_IN_COPY;

/** A routed required provider is safe presentation context, not an error description. */
export function signInErrorCopy(key: SignInErrorKey, requiredProvider?: string | null): string {
  if (key === "method_not_allowed") {
    if (requiredProvider === "microsoft") return "This account signs in with Microsoft.";
    if (requiredProvider === "google") return "This account signs in with Google.";
  }
  return SIGN_IN_COPY[key];
}
export type SignInFailureCategory =
  | "cancelled"
  | "connection"
  | "timed_out"
  | "browser_interaction"
  | "provider_configuration"
  | "account_refused"
  | "verification_failed"
  | "signin_failed";
const CATEGORIES: readonly string[] = [
  "cancelled",
  "connection",
  "timed_out",
  "browser_interaction",
  "provider_configuration",
  "account_refused",
  "verification_failed",
  "signin_failed",
];

export class SignInFailure extends Error {
  constructor(readonly category: SignInFailureCategory) {
    super(SIGN_IN_COPY[category]);
    this.name = "SignInFailure";
  }
}

const LEGACY: Readonly<Record<string, SignInErrorKey>> = {
  ...Object.fromEntries(
    Object.entries(SIGN_IN_COPY)
      .filter(([, text]) => text)
      .map(([key, text]) => [text, key as SignInErrorKey]),
  ),
  "Google sign-in isn't set up.": "provider_configuration",
  "Google sign-in could not start.": "browser_interaction",
  "Google sign-in didn't finish.": "signin_failed",
  "The Google sign-in could not be verified.": "verification_failed",
  "This sign-in expired or was started in another browser. Try again.": "verification_failed",
  "You are not a member of this organization.": "not_invited",
  "This account signs in with Microsoft.": "method_not_allowed",
  "This account signs in with Google.": "method_not_allowed",
  "This account signs in with a password or a passkey.": "method_not_allowed",
  "Sign-in for this account isn't set up. Ask your administrator.": "method_not_allowed",
};

export function signInErrorKey(value: unknown): SignInErrorKey | undefined {
  if (value === undefined || value === null || value === "") return undefined;
  if (typeof value !== "string" || value.length > 512) return "signin_failed";
  if (Object.hasOwn(SIGN_IN_COPY, value))
    return value === "cancelled" ? undefined : (value as SignInErrorKey);
  return Object.hasOwn(LEGACY, value) ? LEGACY[value] : "signin_failed";
}

/**
 * The server could not be reached or a proxy answered for it: no response, a gateway error, or
 * a 5xx without the server's own JSON error (`http_error`). A 5xx the server itself sent with a
 * code (for example `email_unavailable`) is a real answer, not an outage.
 */
export function isUnreachable(code: string, status: number): boolean {
  return (
    status === 0 ||
    code === "network_error" ||
    status === 502 ||
    status === 504 ||
    (status >= 500 && code === "http_error")
  );
}

/** Call only after the caller established a genuine application ApiError. */
export function signInApiErrorKey(code: string, status: number): SignInErrorKey {
  if (status === 426 || code === "update_required") return "update_required";
  if (isUnreachable(code, status)) return "unreachable";
  if (status === 429) return "rate_limited";
  const keys: readonly string[] = [
    "invalid_email",
    "bad_credentials",
    "ticket_expired",
    "code_wrong",
    "code_expired",
    "codes_locked",
    "passkey_required",
    "passkey_failed",
    "challenge_expired",
    "invalid_credential",
    "invalid_handoff",
    "rate_limited",
    "method_not_allowed",
    "not_invited",
    "user_disabled",
    "workspace_required",
    "identity_link_required",
    "account_mismatch",
  ];
  return keys.includes(code) ? (code as SignInErrorKey) : "signin_failed";
}

export function nativeSignInCategory(value: unknown): SignInFailureCategory {
  if (typeof value === "object" && value !== null) {
    const v = value as Record<string, unknown>;
    if (
      Object.keys(v).length === 3 &&
      v.kind === "sign_in_failure" &&
      v.version === 1 &&
      typeof v.category === "string" &&
      CATEGORIES.includes(v.category)
    )
      return v.category as SignInFailureCategory;
  }
  switch (value) {
    case "Sign-in was cancelled.":
      return "cancelled";
    case "Sign-in timed out. Try again.":
      return "timed_out";
    case "Sign-in is not configured in this build.":
      return "provider_configuration";
    case "The sign-in response did not match this request.":
      return "verification_failed";
    default:
      return "signin_failed";
  }
}
